import { toast } from 'react-toastify';

/** Per-item outcome returned by bulk endpoints (`BulkDeleteResult`, `BulkRenameResult`). */
export interface BulkItemResult {
  id: string;

  /** Human-readable name of the record, shown in place of the id. */
  label?: string;

  success: boolean;

  error?: string;
}

/** Record targeted by a bulk action, with the name shown in result toasts. */
export interface BulkItem {
  id: string;

  label?: string;
}

export type BulkVerb = 'delete' | 'rename';

const PAST_TENSE: Record<BulkVerb, string> = {
  delete: 'deleted',
  rename: 'renamed',
};

/** Irregular plurals; everything else just gets an "s". */
const PLURAL_OVERRIDES: Record<string, string> = {
  utility: 'utilities',
};

export const pluralize = (singular: string): string =>
  PLURAL_OVERRIDES[singular] ?? `${singular}s`;

/** `singular` when `count` is 1, otherwise its plural. */
export const label = (count: number, singular: string): string =>
  count === 1 ? singular : pluralize(singular);

/**
 * Surfaces the per-item outcome of a bulk action: a success toast for the
 * records that succeeded, and an error toast naming each record that failed
 * with its reason.
 *
 * @param singular lowercase entity noun (e.g. "meter", "utility"); plural is
 *   derived automatically.
 */
export function notifyBulkResults(
  results: BulkItemResult[],
  singular: string,
  verb: BulkVerb
) {
  const failed = results.filter((result) => !result.success);
  const succeededCount = results.length - failed.length;

  if (succeededCount > 0) {
    toast.success(
      `${succeededCount} ${label(succeededCount, singular)} ${PAST_TENSE[verb]}`
    );
  }

  if (failed.length > 0) {
    const details = failed
      .map(
        (result) =>
          `${result.label ?? result.id}${
            result.error ? ` (${result.error})` : ''
          }`
      )
      .join('; ');

    toast.error(
      `Failed to ${verb} ${failed.length} ${label(
        failed.length,
        singular
      )}: ${details}`,
      { autoClose: 10000 }
    );
  }
}

const errorText = (error: unknown): string => {
  const data = (error as { data?: { errorMessage?: string; message?: string } })
    ?.data;
  return data?.errorMessage ?? data?.message ?? 'Request failed';
};

/**
 * Sends `request` for every item concurrently and reports each response as a
 * result; a response carrying `error` fails only its own item.
 */
export async function settleEach(
  items: BulkItem[],
  request: (
    item: BulkItem
  ) => PromiseLike<{ error?: unknown }> | { error?: unknown }
): Promise<BulkItemResult[]> {
  const responses = await Promise.all(items.map(request));
  return items.map(({ id, label }, i) => {
    const { error } = responses[i];
    return {
      id,
      label,
      success: error == null,
      error: error == null ? undefined : errorText(error),
    };
  });
}
